import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { MIN_GROUP_COUNT } from "../../../utils/breakouts";

import { BreakoutGroupKind } from "components/server-types";
import { t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { Tooltip } from "mds/components/Tooltip";
import { PageIcon, PageListIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectGroupPagesForCurrentSection,
  selectReleasedPageGroupsForCurrentSection,
  selectTopicsForCurrentSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";

interface DropdownMenuItemProps {
  title: string;
}
const DropdownMenuItem: FC<DropdownMenuItemProps> = ({ title }) => (
  <div className="truncate text-sm font-medium" title={title}>
    {title}
  </div>
);

type FormValues = {
  breakout_group_type: BreakoutGroupKind;
  group_page_id: string | null;
  should_sync_breakouts_with_page_groups: boolean;
};

export const GroupPageDropdown: FC = () => {
  // string    → a topicId has been selected
  // null      → no topicId selected yet (initial state)
  // undefined → user is viewing group pages that are not topic-based (e.g., assignments)
  const [topicId, setTopicId] = useState<string | null | undefined>(null);

  const form = useFormContext<FormValues>();
  const breakoutGroupType = form.watch("breakout_group_type");
  const groupPageId = form.watch("group_page_id");
  const shouldSyncBreakoutsWithPageGroups = form.watch("should_sync_breakouts_with_page_groups");

  const sectionId = useAppSelector(selectCurrentSectionId);
  const pages = useListSelector(selectGroupPagesForCurrentSection);
  const topics = useListSelector(selectTopicsForCurrentSection);
  const pageGroups = useListSelector(selectReleasedPageGroupsForCurrentSection);

  const isGroupPageBreakoutGroupType = breakoutGroupType === "page";

  useEffect(() => {
    if (!sectionId) return;
    storeApi.page_groups.list({ section_id: sectionId });
  }, [sectionId]);

  const topicOptions = useMemo(
    () =>
      topics.map((topic) => ({
        title: <DropdownMenuItem title={topic.title} />,
        id: topic.id,
        iconStart: <PageListIcon />,
      })),
    [topics],
  );

  const topicOptionsWithNoTopicOption = [
    ...topicOptions,
    {
      title: <DropdownMenuItem title={t("breakout_group_modal.no_topic")} />,
      id: "none",
    },
  ];

  const pageGroupCountByPage = useMemo(() => {
    return pageGroups.reduce(
      (acc, group) => {
        return {
          ...acc,
          [group.page_id]: (acc[group.page_id] || 0) + 1,
        };
      },
      {} as Record<string, number>,
    );
  }, [pageGroups]);

  const pageOptions = useMemo(
    () =>
      pages
        // Filter pages based on the selected topic
        .filter((page) => page.topic_id === topicId)
        // Filter out pages that do not have enough groups (but it's ok to have less groups if syncing is enabled)
        .filter(
          (page) =>
            shouldSyncBreakoutsWithPageGroups || pageGroupCountByPage[page.id] >= MIN_GROUP_COUNT,
        )
        .map((page) => ({
          title: <DropdownMenuItem title={page.title} />,
          id: page.id,
          iconStart: <PageIcon />,
        })),
    [pages, topicId, pageGroupCountByPage, shouldSyncBreakoutsWithPageGroups],
  );

  const isGroupPageDropdownDisabled =
    !isGroupPageBreakoutGroupType || topicId === null || pageOptions.length === 0;

  return (
    <div className="flex gap-2">
      <DropdownMenu
        buttonText={
          topicId === null ? (
            <div className="text-sm font-medium">{t("breakout_group_modal.select_a_topic")}</div>
          ) : undefined
        }
        className="ml-6 !h-full !w-full !max-w-[43%] !justify-between !p-1.5"
        disabled={!isGroupPageBreakoutGroupType}
        eventName="Breakouts Setup - Select a topic"
        items={topicOptionsWithNoTopicOption}
        menuItemKind="topic-dropdown"
        selectedValue={topicId}
        truncateWidth="max-w-l"
        onSelectionChange={(selectedTopic) => {
          if (selectedTopic.id !== topicId) {
            form.setValue("group_page_id", null);
          }
          setTopicId(selectedTopic.id);
        }}
      />

      <DropdownMenu
        buttonText={
          !groupPageId ? (
            <Tooltip
              content={
                isGroupPageDropdownDisabled ? t("tooltip.no_group_page_available") : undefined
              }
            >
              <div className="text-sm font-medium">
                {t("breakout_group_modal.select_a_group_page")}
              </div>
            </Tooltip>
          ) : undefined
        }
        className="mr-6 !h-full !w-full !max-w-[43%] !justify-between !p-1.5"
        disabled={isGroupPageDropdownDisabled}
        eventName="Breakouts Setup - Select a group page"
        eventPropertyKey="pageId"
        items={pageOptions}
        menuItemKind="page-dropdown"
        selectedValue={groupPageId}
        truncateWidth="max-w-l"
        onSelectionChange={(selectedPage) => {
          if (selectedPage.id) {
            form.clearErrors("group_page_id");
          }
          form.setValue("group_page_id", selectedPage.id);
        }}
      />
    </div>
  );
};
